/**
 * HallOfFameScreen - Écran du Hall of Fame
 * Identique à Flutter BaseHallOfFameWidget
 */

import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faTimes, faTrophy} from '@fortawesome/free-solid-svg-icons';
import {usePlayer} from '../contexts/PlayerContext';
import gameStateService from '../services/gameStateService';
import {GameState} from '../models/GameState';

const HallOfFameScreen: React.FC = () => {
  const navigation = useNavigation();
  const {currentAdventure} = usePlayer();
  const [bestScores, setBestScores] = useState<GameState[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBestScores();
  }, [currentAdventure]);

  const loadBestScores = async () => {
    if (!currentAdventure) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Récupérer tous les gameStates pour cette aventure
      const allStates = await gameStateService.getGameStatesByAdventure(
        currentAdventure.uniqueURI,
      );

      // Filtrer les états réussis (succeed)
      const succeededStates = allStates.filter(
        (state) => state.state === 'SUCCEED' && state.data.gameSuccess,
      );

      // Trier par score final (si disponible)
      const sorted = succeededStates.sort((a, b) => {
        const scoreA = a.data.gameSuccess?.scoreFinal || a.data.gameSuccess?.note || 0;
        const scoreB = b.data.gameSuccess?.scoreFinal || b.data.gameSuccess?.note || 0;
        return scoreB - scoreA;
      });

      // Prendre les 10 meilleurs
      setBestScores(sorted.slice(0, 10));
    } catch (error) {
      console.error('[HallOfFameScreen] Error loading best scores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigation.goBack();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#ffd700" />
          <Text style={styles.loadingText}>Chargement...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <View style={styles.header}>
          <FontAwesomeIcon icon={faTrophy} size={32} color="#ffd700" />
          <Text style={styles.title}>Hall of Fame</Text>
          <TouchableOpacity style={styles.closeButton} onPress={handleBack}>
            <FontAwesomeIcon icon={faTimes} size={24} color="#fff" />
          </TouchableOpacity>
        </View>

        {bestScores.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>Aucun score enregistré</Text>
            <Text style={styles.emptySubtext}>
              Complétez une aventure pour apparaître ici !
            </Text>
          </View>
        ) : (
          <View style={styles.scoresContainer}>
            {bestScores.map((state, index) => {
              const score = state.data.gameSuccess?.scoreFinal || state.data.gameSuccess?.note || 0;
              const date = formatDate(state.lastUpdate);
              const isFirst = index === 0;

              return (
                <View
                  key={index}
                  style={[
                    styles.scoreItem,
                    isFirst && {
                      borderWidth: 2,
                      borderColor: '#ffd700',
                    },
                  ]}>
                  <View style={styles.rankContainer}>
                    <Text style={styles.rank}>#{index + 1}</Text>
                  </View>
                  <View style={styles.scoreContent}>
                    <Text style={styles.scoreValue}>{score}</Text>
                    <Text style={styles.scoreDate}>{date}</Text>
                    {state.data.gameSuccess?.note && (
                      <Text style={styles.scoreNote}>Note: {state.data.gameSuccess.note}/20</Text>
                    )}
                  </View>
                  {isFirst && (
                    <View style={styles.trophyContainer}>
                      <FontAwesomeIcon icon={faTrophy} size={24} color="#ffd700" />
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#18191A',
  },
  contentContainer: {
    flexGrow: 1,
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 30,
    position: 'relative',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffd700',
    letterSpacing: 1.2,
    marginLeft: 12,
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#fff',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 12,
  },
  emptySubtext: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center',
  },
  scoresContainer: {
    flex: 1,
  },
  scoreItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  rankContainer: {
    width: 50,
    alignItems: 'center',
    marginRight: 16,
  },
  rank: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffd700',
  },
  scoreContent: {
    flex: 1,
  },
  scoreValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 4,
  },
  scoreDate: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: 4,
  },
  scoreNote: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },
  trophyContainer: {
    marginLeft: 12,
  },
});

export default HallOfFameScreen;
